import React, { useState, useEffect } from 'react';

const AnexoSplitModal = ({ isOpen, onClose, scanResult, onConfirm }) => {
  const [selectedSeqs, setSelectedSeqs] = useState(new Set());
  const [manualSeqs,   setManualSeqs]   = useState(new Set());
  const [selectValue,  setSelectValue]  = useState('');

  // Initialize selections whenever scanResult changes (modal re-opens for a new manifest)
  useEffect(() => {
    if (scanResult) {
      setSelectedSeqs(new Set(scanResult.highValue.map(p => p.seq)));
      setManualSeqs(new Set());
      setSelectValue('');
    }
  }, [scanResult]);

  if (!isOpen || !scanResult) return null;

  const { threshold, all, highValue } = scanResult;

  // Items available in the dropdown: all packages minus already-selected seqs
  const dropdownItems = all.filter(p => !selectedSeqs.has(p.seq));

  const handleToggle = (seq) => {
    const isChecked = selectedSeqs.has(seq);
    setSelectedSeqs(prev => {
      const next = new Set(prev);
      if (isChecked) {
        next.delete(seq);
      } else {
        next.add(seq);
      }
      return next;
    });
    // If unchecking a manually-added item, remove it from the manual list too
    // so it disappears from the display and returns to the dropdown
    if (isChecked && manualSeqs.has(seq)) {
      setManualSeqs(prev => {
        const next = new Set(prev);
        next.delete(seq);
        return next;
      });
    }
  };

  const handleAddManual = (e) => {
    const seq = parseInt(e.target.value, 10);
    if (!seq) return;
    setSelectedSeqs(prev => new Set([...prev, seq]));
    setManualSeqs(prev => new Set([...prev, seq]));
    setSelectValue('');
  };

  const handleConfirm = () => {
    if (selectedSeqs.size === 0) return;
    onConfirm(Array.from(selectedSeqs));
  };

  // Combined display list: auto-flagged items first, then manually added items not already in highValue
  const allDisplayItems = [
    ...highValue,
    ...all.filter(p => manualSeqs.has(p.seq) && !highValue.some(h => h.seq === p.seq)),
  ];

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000,
    }}>
      <div className="tile" style={{
        width: '100%', maxWidth: '560px', margin: '16px',
        maxHeight: '90vh', display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 700 }}>
              Anexo Individual — Paquetes detectados
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'var(--text-tertiary)' }}>
              Umbral: ${threshold.toFixed(2)} USD
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: '18px', color: 'var(--text-tertiary)', padding: '4px',
              lineHeight: 1, flexShrink: 0,
            }}
          >✕</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
          <p style={{ margin: '0 0 12px', fontSize: '12px', color: 'var(--text-tertiary)' }}>
            Los siguientes paquetes superan el umbral y se generarán como Anexos individuales.
            Desmarca para excluirlos.
          </p>

          {allDisplayItems.map(pkg => {
            const isManual  = manualSeqs.has(pkg.seq);
            const isChecked = selectedSeqs.has(pkg.seq);
            return (
              <div
                key={pkg.seq}
                onClick={() => handleToggle(pkg.seq)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  padding: '8px 12px', marginBottom: '6px',
                  border: '1px solid var(--border)', borderRadius: '8px',
                  background: isChecked ? 'var(--blue-soft, #eff6ff)' : 'transparent',
                  cursor: 'pointer', userSelect: 'none',
                }}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => handleToggle(pkg.seq)}
                  onClick={e => e.stopPropagation()}
                  style={{ width: '16px', height: '16px', cursor: 'pointer', flexShrink: 0 }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: '13px', fontWeight: 600, color: 'var(--text)',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    #{pkg.seq} — {pkg.label}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '2px' }}>
                    {pkg.recipient}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                  <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--blue)' }}>
                    ${pkg.total.toFixed(2)}
                  </span>
                  {isManual && (
                    <span style={{
                      fontSize: '10px', fontWeight: 700, padding: '2px 6px', borderRadius: '99px',
                      background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a',
                    }}>manual</span>
                  )}
                </div>
              </div>
            );
          })}

          {/* Add another package */}
          {dropdownItems.length > 0 && (
            <div style={{ marginTop: '16px' }}>
              <label style={{
                fontSize: '11px', fontWeight: 700, color: 'var(--text-tertiary)',
                textTransform: 'uppercase', letterSpacing: '0.05em',
                display: 'block', marginBottom: '6px',
              }}>
                Agregar otro paquete
              </label>
              <select
                value={selectValue}
                onChange={handleAddManual}
                style={{
                  width: '100%', padding: '8px 10px',
                  border: '1px solid var(--border)', borderRadius: '8px',
                  fontSize: '13px', background: 'var(--bg)', color: 'var(--text)',
                  cursor: 'pointer',
                }}
              >
                <option value="">— Seleccionar paquete —</option>
                {dropdownItems.map(p => (
                  <option key={p.seq} value={p.seq}>
                    #{p.seq} — {p.label} (${p.total.toFixed(2)})
                  </option>
                ))}
              </select>
            </div>
          )}

          {selectedSeqs.size === 0 && (
            <p style={{ margin: '12px 0 0', fontSize: '12px', color: '#c2410c', fontStyle: 'italic' }}>
              Selecciona al menos un paquete o cancela para generar Anexo normal.
            </p>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 20px', borderTop: '1px solid var(--border)',
          display: 'flex', justifyContent: 'flex-end', gap: '8px',
        }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '8px 16px', background: 'transparent',
              border: '1px solid var(--border)', borderRadius: '8px',
              fontSize: '13px', fontWeight: 600, cursor: 'pointer', color: 'var(--text)',
            }}
          >Cancelar</button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={selectedSeqs.size === 0}
            style={{
              padding: '8px 16px', background: 'var(--blue)', color: '#fff',
              border: 'none', borderRadius: '8px',
              fontSize: '13px', fontWeight: 600,
              cursor: selectedSeqs.size === 0 ? 'not-allowed' : 'pointer',
              opacity: selectedSeqs.size === 0 ? 0.5 : 1,
            }}
          >Confirmar y generar</button>
        </div>
      </div>
    </div>
  );
};

export default AnexoSplitModal;
